import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraPageHeader, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RegistryEntityPicker } from '../../../components/SharedRecord/RegistryEntityPicker';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { ErrorState } from '../../Delivery/_shared/components/PageStates';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { useOrgSeats, usePeopleMoves, usePersonCareer } from './hooks';
import { MovesView, sinceYearOf } from './MovesView';
import { currentYear, holderAt, orgParam, parseOrg } from './model';
import { OrgChartView } from './OrgChartView';
import { PERSON_HINT_SECTION, personRail } from './PersonPanel';
import { SeatDialogs } from './SeatEditor';
const T = Testids.peopleMovement;
function readYear(raw) {
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1900 && n <= currentYear() ? n : currentYear();
}
function readSpan(raw) {
    return raw && /^since-\d{4}$/.test(raw) ? raw : 'all';
}
export function PeopleMovementPage() {
    const [params, setParams] = useSearchParams();
    const areas = useTenantAreas();
    const canEdit = areas.registries;
    const canDelete = canEdit && areas.canManageCompanySettings;
    const view = params.get('view') === 'flow' ? 'flow' : 'chart';
    const org = parseOrg(params.get('org'));
    const year = readYear(params.get('asOf'));
    const personId = params.get('person') ?? undefined;
    const span = readSpan(params.get('span'));
    const acquisitions = params.get('acq') === '1';
    const [seatId, setSeatId] = useState();
    const [dialog, setDialog] = useState();
    const patch = (changes)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            for (const [k, v] of Object.entries(changes)){
                if (v === undefined || v === '') next.delete(k);
                else next.set(k, v);
            }
            return next;
        }, {
            replace: true
        });
    const seats = useOrgSeats(view === 'chart' ? org : undefined, year);
    const moves = usePeopleMoves(acquisitions, sinceYearOf(span));
    const career = usePersonCareer(personId);
    const selectedSeat = seats.data?.seats.find((s)=>s.id === seatId);
    // A retired or deleted seat leaves the loaded list; drop the stale selection.
    useEffect(()=>{
        if (seatId && seats.data && !selectedSeat) setSeatId(undefined);
    }, [
        seatId,
        seats.data,
        selectedSeat
    ]);
    const selectPerson = (contactId)=>patch({
            person: contactId
        });
    const pickOrg = (next)=>{
        setSeatId(undefined);
        patch({
            org: orgParam(next),
            person: undefined
        });
    };
    const rail = [];
    if (canEdit && view === 'chart' && org && selectedSeat) {
        rail.push({
            id: 'seat',
            title: 'Seat',
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        "data-testid": T.editSeat,
                        onClick: ()=>setDialog({
                                kind: 'edit',
                                seat: selectedSeat
                            }),
                        children: "Edit seat"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        "data-testid": T.assignPerson,
                        onClick: ()=>setDialog({
                                kind: 'assign',
                                seat: selectedSeat
                            }),
                        children: "Assign person"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        "data-testid": T.retireSeat,
                        onClick: ()=>setDialog({
                                kind: 'retire',
                                seat: selectedSeat
                            }),
                        children: "Retire seat"
                    }),
                    canDelete && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        "data-testid": T.deleteSeat,
                        onClick: ()=>setDialog({
                                kind: 'delete',
                                seat: selectedSeat
                            }),
                        children: "Delete seat"
                    })
                ]
            })
        });
    }
    if (personId && career.data) rail.push(...personRail(career.data));
    else if (personId && career.isError) rail.push({
        id: 'person-error',
        title: 'Person',
        children: /*#__PURE__*/ _jsx(SpecraText, {
            as: "p",
            size: "body-ui",
            tone: "muted",
            children: "This person's career could not be loaded."
        })
    });
    else if (!personId) rail.push(PERSON_HINT_SECTION);
    const body = ()=>{
        if (view === 'flow') {
            if (moves.isPending) return /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading moves"
            });
            if (moves.isError) return /*#__PURE__*/ _jsx(ErrorState, {
                error: moves.error,
                title: "Moves could not be loaded",
                onRetry: ()=>void moves.refetch()
            });
            return /*#__PURE__*/ _jsx(MovesView, {
                data: moves.data,
                span: span,
                onSpanChange: (s)=>patch({
                        span: s === 'all' ? undefined : s
                    }),
                acquisitions: acquisitions,
                onAcquisitionsChange: (on)=>patch({
                        acq: on ? '1' : undefined
                    }),
                onSelectPerson: selectPerson
            });
        }
        if (!org) return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            framed: true,
            title: "Pick an organization",
            description: "Search for a company or government office to see who holds which job there."
        });
        if (seats.isPending) return /*#__PURE__*/ _jsx(SpecraSpinner, {
            label: "Loading seats"
        });
        if (seats.isError) return /*#__PURE__*/ _jsx(ErrorState, {
            error: seats.error,
            title: "Seats could not be loaded",
            onRetry: ()=>void seats.refetch()
        });
        return /*#__PURE__*/ _jsx(OrgChartView, {
            data: seats.data,
            year: year,
            onYearChange: (y)=>patch({
                    asOf: y === currentYear() ? undefined : String(y)
                }),
            selectedSeatId: seatId,
            onSelectSeat: (seat)=>{
                setSeatId(seat.id);
                const holder = holderAt(seat, year);
                if (holder) selectPerson(holder.contactId);
            },
            onSelectPerson: selectPerson,
            canEdit: canEdit,
            onAddSeat: ()=>setDialog({
                    kind: 'add'
                })
        });
    };
    return /*#__PURE__*/ _jsxs(RegistryRecordFrame, {
        kindLabel: "People movement",
        tab: "contacts",
        title: "People movement",
        status: "ready",
        railSections: rail,
        railLabel: "Person details",
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "ui",
            title: "People movement",
            description: "Who holds which job at the companies and agencies we track, and where people came from and went. Built from the people registry; every move has a source.",
            actions: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "View",
                testId: T.viewToggle,
                value: view,
                options: [
                    {
                        value: 'chart',
                        label: 'Org chart over time'
                    },
                    {
                        value: 'flow',
                        label: 'Movement between organizations'
                    }
                ],
                onValueChange: (v)=>patch({
                        view: v === 'chart' ? undefined : v
                    })
            })
        }),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-[var(--space-4)]",
                "data-testid": T.page,
                children: [
                    view === 'chart' && /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-[var(--space-3)]",
                        "data-testid": T.orgPicker,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "min-w-64",
                                children: /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                                    kinds: [
                                        'COMPANY',
                                        'GOVERNMENT_ORG'
                                    ],
                                    createMode: "none",
                                    label: "Organization",
                                    placeholder: seats.data?.employer?.name || 'Search companies and offices',
                                    selectedLabel: seats.data?.employer?.name,
                                    value: org?.id,
                                    onPick: (o)=>pickOrg({
                                            kind: o.kind,
                                            id: o.registryId
                                        })
                                })
                            }),
                            canEdit && org && !seats.data?.seatsNotAllowed && /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                size: "sm",
                                "data-testid": T.addSeat,
                                onClick: ()=>setDialog({
                                        kind: 'add'
                                    }),
                                children: "Add seat"
                            })
                        ]
                    }),
                    body()
                ]
            }),
            dialog && org && /*#__PURE__*/ _jsx(SeatDialogs, {
                org: org,
                seats: seats.data?.seats ?? [],
                state: dialog,
                onClose: ()=>setDialog(undefined)
            })
        ]
    });
}
